export type RecordingUiState =
  | 'idle'
  | 'preparing'
  | 'starting'
  | 'active'
  | 'stopping'
  | 'failed'
  | 'cancelled';

export type RecordingStatusPayload = {
  state: RecordingUiState;
  requestId?: string;
  egressId?: string | null;
  queuePosition?: number;
  isRecordingFile: boolean;
  updatedAt?: string;
  failureReason?: string | null;
  message?: string;
};

/** Evita N componentes batendo LiveKit ao mesmo tempo (travava next-server + UI). */
const STATUS_CLIENT_TTL_MS = 2_500;
const statusClientCache = new Map<
  string,
  { at: number; value?: RecordingStatusPayload; inflight?: Promise<RecordingStatusPayload> }
>();

export async function fetchRecordingStatus(roomName: string): Promise<RecordingStatusPayload> {
  const now = Date.now();
  const cached = statusClientCache.get(roomName);
  if (cached?.value && now - cached.at < STATUS_CLIENT_TTL_MS) {
    return cached.value;
  }
  if (cached?.inflight) {
    return cached.inflight;
  }

  const inflight = (async () => {
    try {
      const res = await fetch(`/api/record/status?roomName=${encodeURIComponent(roomName)}`);
      if (!res.ok) throw new Error(`Status HTTP ${res.status}`);
      const value = (await res.json()) as RecordingStatusPayload;
      statusClientCache.set(roomName, { at: Date.now(), value });
      return value;
    } catch (err) {
      statusClientCache.delete(roomName);
      throw err;
    }
  })();

  statusClientCache.set(roomName, { at: cached?.at ?? 0, value: cached?.value, inflight });
  return inflight;
}

/** Invalida cache local após start/stop (próximo fetch vai ao servidor). */
export function invalidateRecordingStatusCache(roomName?: string) {
  if (roomName) {
    statusClientCache.delete(roomName);
    return;
  }
  statusClientCache.clear();
}

export type StartRecordingOpts = {
  requestedByIdentity?: string;
  requestedByName?: string;
};

type LocalParticipantLike = {
  identity?: string;
  name?: string | null;
  trackPublications?: { values(): Iterable<{ track?: unknown; isMuted?: boolean; trackName?: string; name?: string }> };
  publishTrack?: (track: MediaStreamTrack, opts?: { name?: string }) => Promise<unknown>;
} | null | undefined;

/** Metadados do participante local para POST /api/record/start (audit + meet-api). */
export function getStartRecordingOptsFromRoom(
  room: { localParticipant?: LocalParticipantLike } | null | undefined,
): StartRecordingOpts {
  return {
    requestedByIdentity: room?.localParticipant?.identity ?? undefined,
    requestedByName: room?.localParticipant?.name ?? undefined,
  };
}

export async function postStartRecording(
  roomName: string,
  opts?: StartRecordingOpts,
): Promise<RecordingStatusPayload & { httpStatus: number }> {
  invalidateRecordingStatusCache(roomName);
  const res = await fetch('/api/record/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      roomName,
      requestedByIdentity: opts?.requestedByIdentity,
      requestedByName: opts?.requestedByName,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as RecordingStatusPayload;
  statusClientCache.set(roomName, { at: Date.now(), value: body });
  return { ...body, httpStatus: res.status };
}

type StartFromRoom = {
  name?: string | null;
  localParticipant?: LocalParticipantLike;
  remoteParticipants?: {
    values(): Iterable<{
      trackPublications?: {
        values(): Iterable<{ track?: unknown; isMuted?: boolean; trackName?: string; name?: string }>;
      };
    }>;
  };
};

/**
 * Clique / retry: publica keepalive silencioso se a sala ainda não tem mídia,
 * depois pede o start. O compositor default do LiveKit aborta sem track.
 */
export async function startRecordingFromRoom(
  room: StartFromRoom | null | undefined,
): Promise<RecordingStatusPayload & { httpStatus: number }> {
  const roomName = room?.name?.trim() ?? '';
  if (!roomName) {
    return {
      state: 'failed',
      isRecordingFile: false,
      httpStatus: 400,
      message: 'Sala inválida',
    };
  }
  try {
    const { prepareRoomForRecording } = await import('./prepare-room-for-recording');
    await prepareRoomForRecording(room);
  } catch (error) {
    console.warn('[record] prepareRoomForRecording failed; starting anyway', error);
  }
  return postStartRecording(roomName, getStartRecordingOptsFromRoom(room));
}

export async function postCancelRecording(roomName: string): Promise<void> {
  invalidateRecordingStatusCache(roomName);
  await fetch('/api/record/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomName }),
  });
}

export async function postAutoRestartOptOut(roomName: string): Promise<void> {
  await fetch('/api/record/auto-restart-opt-out', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomName }),
  });
}

export type RecoverRecordingClientResult = {
  recovered: boolean;
  synced: boolean;
  error?: string;
};

/** Recover S3 só com sala ociosa — o servidor recusa se houver gravação em andamento. */
export async function postRecoverRecording(roomName: string): Promise<RecoverRecordingClientResult> {
  const res = await fetch('/api/record/recover', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomName }),
  });
  const body = (await res.json().catch(() => ({}))) as RecoverRecordingClientResult;
  return {
    recovered: body.recovered === true,
    synced: body.synced === true,
    error: typeof body.error === 'string' ? body.error : undefined,
  };
}

/**
 * Vermelho / "está gravando de verdade".
 * `starting` NÃO entra — o egress ainda pode estar subindo e o arquivo ainda não existe.
 */
export function isRecordingVisualState(state: RecordingUiState): boolean {
  return state === 'active';
}

/** Fila / provisionamento — único estado em que cancelar a solicitação é seguro. */
export function isPreparingVisualState(state: RecordingUiState): boolean {
  return state === 'preparing';
}

/** Egress pedido, ainda sem confirmação de arquivo. */
export function isRecordingStartingState(state: RecordingUiState): boolean {
  return state === 'starting';
}

/** Banner de espera (preparing ou starting) — ainda não há arquivo. */
export function isAwaitingFileRecordingState(state: RecordingUiState): boolean {
  return state === 'preparing' || state === 'starting';
}

/** Não iniciar outra gravação (nem recover S3) enquanto estes estados existirem. */
export function isRecordingBusyState(state: RecordingUiState): boolean {
  return (
    state === 'preparing' ||
    state === 'starting' ||
    state === 'active' ||
    state === 'stopping'
  );
}

/**
 * Único critério para toast "Gravação iniciada", timer e borda vermelha.
 * Exige ACTIVE + arquivo + egressId — sem isso o poll deve continuar.
 */
export function isConfirmedFileRecording(
  status: Pick<RecordingStatusPayload, 'state' | 'isRecordingFile' | 'egressId'>,
): boolean {
  return (
    status.state === 'active' &&
    status.isRecordingFile === true &&
    typeof status.egressId === 'string' &&
    status.egressId.length > 0
  );
}

export type RecordingWindowEvent =
  | 'recording:preparing'
  | 'recording:starting'
  | 'recording:start'
  | 'recording:stop';

/** Evento de janela alinhado ao estado — `starting` não dispara `recording:start`. */
export function windowEventForRecordingUiState(state: RecordingUiState): RecordingWindowEvent {
  if (state === 'preparing') return 'recording:preparing';
  if (state === 'starting') return 'recording:starting';
  if (state === 'active' || state === 'stopping') return 'recording:start';
  return 'recording:stop';
}

/** Estado remoto `stopping` sem egress ativo é órfão — UI deve tratar como idle. */
export function normalizeRecordingUiState(
  status: Pick<RecordingStatusPayload, 'state' | 'isRecordingFile'>,
): RecordingUiState {
  if (status.state === 'stopping' && !status.isRecordingFile) {
    return 'idle';
  }
  return status.state;
}
